import {
  BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput,
  ShiftManagementBulkCreateUnassignedShiftsDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import buildAddUnassignedShiftsParameters from 'modules/shift_management/lib/buildAddUnassignedShiftParameters'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackAddUnassignedEvent } from 'modules/shift_management/lib/trackEvents'
import { updateUnassignedShiftsCoverageCache } from 'modules/shift_management/utils/cache/unassignedShiftsCoverage'

import { useFilters } from './useFilters'
import { useSchedule } from './useSchedule'

interface Props {
  form: UnassignedShiftForm
  startDate: moment.Moment
  endDate: moment.Moment
  companyId: string
  staffRequirements?: Array<{
    job_catalog_roles_ids: string[]
    job_catalog_tree_role_uuids: string[]
    staff_count: number
  }>
}

type StaffRequirement = {
  job_catalog_roles_ids: string[]
  job_catalog_tree_role_uuids: string[]
  staff_count: number
}

const convertToString = (value: string): string | null => (value ? value : null)

export const buildUnassignedShiftsParams = ({
  form,
  companyId,
  startDate,
  endDate,
  staffRequirements = form.get('staff_requirements').value,
}: Props): BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput[] => {
  // Important: Clone the date to avoid mutating the original date
  let currentDate = startDate.clone()
  const unassignedShiftParams: BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput[] = []

  while (currentDate <= endDate) {
    staffRequirements.forEach((requirement: StaffRequirement) => {
      unassignedShiftParams.push(
        ...buildAddUnassignedShiftsParameters({
          date: currentDate,
          unparsedStartTime: form.get('start_at').value,
          unparsedEndTime: form.get('end_at').value,
          defaultShiftId: convertToString(form.get('id').value),
          extraHours: !!form.get('extra_hours').value,
          notes: form.get('notes').value,
          counter: +requirement.staff_count,
          jobCatalogRolesIds: [...requirement.job_catalog_roles_ids],
          jobCatalogTreeRoleUuids: [...requirement.job_catalog_tree_role_uuids],
          overnightShift: form.isOvernight || form.get('overnight_shift').value,
          allCompany: form.get('all_company').value,
          companyId,
          locationId: convertToString(form.get('location_id').value),
          workAreaId: convertToString(form.get('work_area_id').value),
          teamsIdsVisibilities: convertToString(form.get('teams_ids_visibilities').value),
          locationsIdsVisibilities: convertToString(form.get('locations_ids_visibilities').value),
        })
      )
    })

    currentDate = currentDate.add(1, 'day')
  }

  return unassignedShiftParams
}

const useAddUnassignedShifts = ({ form, companyId }: Props) => {
  const [{ view }] = useSchedule()
  const [{ locationIds }] = useFilters()

  const [addUnassignedShifts, { loading }] = useMutation(
    ShiftManagementBulkCreateUnassignedShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementUnassignedShift,
      update: (cache, { data }) => {
        const newUnassignedShifts =
          data?.shiftManagementMutations.bulkCreateShiftmanagementUnassignedShift.unassignedShifts
        if (!newUnassignedShifts || newUnassignedShifts.length === 0) return

        updateUnassignedShiftsCoverageCache({
          cache,
          startAt: view.startAt,
          endAt: view.endAt,
          locationIds,
          unassignedShifts: newUnassignedShifts,
        })
      },
    }
  )

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const unassignedShiftParams = buildUnassignedShiftsParams({
      form,
      companyId,
      startDate: moment(form.get('start_date').value),
      endDate: moment(form.get('end_date').value),
    })
    trackAddUnassignedEvent({ unassignedShiftParams, isDefaultShift: false })
    const result = await addUnassignedShifts({
      variables: { unassignedShifts: unassignedShiftParams },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.add_unassigned_shifts_success'),
      })

      closePortal()
    }
  }

  return {
    submitting: loading,
    onSubmit,
  }
}

export { useAddUnassignedShifts }
